import { FC } from 'react';
import { createUseStyles } from 'react-jss';
import { IOutcomeMetric, OutcomeMetricId } from '@innerhive-internal/innerhive-common';
import { BRAND, CHART_INK, STATUS } from './agencyChartTheme';
import { HistoryWaitNote } from './HistoryWaitNote';

const useStyles = createUseStyles({
    report: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: 14
    },

    grid: {
        display: 'grid',
        // Three cards share a row in the reading column.
        gridTemplateColumns: 'repeat(auto-fit, minmax(185px, 1fr))',
        gap: 14
    },

    card: {
        display: 'flex',
        flexDirection: 'column' as const,
        padding: '15px 16px 14px',
        borderRadius: 12,
        border: `1px solid ${BRAND.tealEdge}`,
        background: 'var(--ion-background-color)',

        '@media print': {
            breakInside: 'avoid'
        }
    },

    // Two figures in one card are set apart by a rule, so each keeps its own title and caption.
    figure: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: 6,

        '& + &': {
            marginTop: 14,
            paddingTop: 14,
            borderTop: `1px solid ${BRAND.tealEdge}`
        }
    },

    title: {
        fontSize: 13,
        fontWeight: 600,
        letterSpacing: '0.02em',
        color: BRAND.deepTeal
    },

    // Every figure is the good news the caseload can show for itself, so every one reads in the
    // steady green; a client's reading colour never stands for a figure.
    value: {
        fontSize: 34,
        fontWeight: 600,
        lineHeight: 1.05,
        color: STATUS.steady,
        fontVariantNumeric: 'tabular-nums'
    },

    caption: {
        fontSize: 12.5,
        lineHeight: 1.4,
        color: CHART_INK.label
    },

    // In the figure's place when there is nobody to count, so the card says why rather than showing a
    // zero that reads as a finding.
    noValue: {
        fontSize: 15,
        fontWeight: 600,
        lineHeight: 1.35,
        color: CHART_INK.label
    },

    more: {
        alignSelf: 'flex-start',
        marginTop: 4,
        padding: 0,
        border: 0,
        background: 'none',
        font: 'inherit',
        fontSize: 11.5,
        fontWeight: 500,
        color: BRAND.deepTeal,
        cursor: 'pointer',

        '&:hover, &:focus-visible': {
            textDecoration: 'underline'
        },

        '@media print': {
            display: 'none'
        }
    }
});

/**
 * Which card each figure sits in: the two about declines and incidents share the first, and the
 * figure about the record stands on its own at the end. Exhaustive, so a new figure has to be placed.
 */
const CARD_OF: { [key in OutcomeMetricId]: number } = {
    'back-from-a-decline': 0,
    'no-incident-recorded': 0,
    'settled-below-usual': 1,
    'family-entries': 2
};

/** The way to the whole list behind the figure, named for what the list holds. */
const SEE_ALL: { [key in OutcomeMetricId]: string } = {
    'back-from-a-decline': 'See all',
    'no-incident-recorded': 'See all incidents',
    'settled-below-usual': 'See all',
    'family-entries': 'See each client'
};

/**
 * One figure: its number, what it counts, and the way to the people behind it, where how the figure
 * is counted is a press away. The card names nobody itself: the list does.
 */
const OutcomeFigure: FC<{ metric: IOutcomeMetric; onOpen: (metric: IOutcomeMetric) => void }> = ({ metric, onOpen }) => {
    const classes = useStyles();

    return (
        <div className={classes.figure} data-id="outcome-metric" data-metric={metric.id}>
            <span className={classes.title}>{metric.title}</span>

            {metric.value === undefined
                ? <span className={classes.noValue}>{metric.caption}</span>
                : (
                    <>
                        <span className={classes.value} data-id="outcome-value">{metric.value}</span>
                        <span className={classes.caption}>{metric.caption}</span>
                    </>
                )}

            <button type="button" className={classes.more} data-id="outcome-see-all" onClick={() => onOpen(metric)}>
                {SEE_ALL[metric.id]} &rsaquo;
            </button>
        </div>
    );
};

/** The outcome figures, and under them what the ones still waiting on the caseload's history are waiting for. */
export const OutcomesReport: FC<{
    metrics: IOutcomeMetric[];
    /** What the figures still waiting on the caseload's history are waiting for, under the ones shown. */
    pending?: string;
    onOpen: (metric: IOutcomeMetric) => void;
}> = ({ metrics, pending, onOpen }) => {
    const classes = useStyles();
    const cards: IOutcomeMetric[][] = [];
    for (const metric of metrics) {
        (cards[CARD_OF[metric.id]] ??= []).push(metric);
    }
    const shownCards = cards.filter(shown => shown.length > 0);

    return (
        <div className={classes.report}>
            {shownCards.length > 0 && (
                <div className={classes.grid} data-id="outcomes-report">
                    {shownCards.map(shown => (
                        <div key={shown[0].id} className={classes.card} data-id="outcome-card">
                            {shown.map(metric => <OutcomeFigure key={metric.id} metric={metric} onOpen={onOpen} />)}
                        </div>
                    ))}
                </div>
            )}

            {pending && <HistoryWaitNote panel="outcomes">{pending}</HistoryWaitNote>}
        </div>
    );
};
